/** Stripes (subcategories) are derived from Supabase rows: distinct list_id, order, labels. */

export function humanizeListId(id) {
  if (id == null || id === "") return "";
  const s = String(id).replace(/_/g, " ");
  return s.replace(/([a-z])([A-Z])/g, "$1 $2");
}

function enrichStoryItem(item, columnType) {
  if (columnType !== "story" || !item?.storyline) return item;
  if (typeof item.truncated === "boolean") return item;
  const words = `${item.title || ""} ${item.storyline}`.split(/\s+/).filter(Boolean);
  return { ...item, truncated: words.length > 100 };
}

/** Stable order so shared `event` query (id or index) matches after reload. */
function sortRowsForColumnDisplay(rows) {
  return [...rows].sort((a, b) => {
    const sa =
      typeof a.sort_order === "number" && !Number.isNaN(a.sort_order)
        ? a.sort_order
        : parseInt(String(a.sort_order ?? ""), 10) || 0;
    const sb =
      typeof b.sort_order === "number" && !Number.isNaN(b.sort_order)
        ? b.sort_order
        : parseInt(String(b.sort_order ?? ""), 10) || 0;
    if (sa !== sb) return sa - sb;
    const da = String(a.starts_at ?? "");
    const db = String(b.starts_at ?? "");
    if (da !== db) return da.localeCompare(db);
    return String(a.id ?? "").localeCompare(String(b.id ?? ""));
  });
}

/**
 * One pass O(n): build sorted list descriptors (id, label, sort, type, category) from API rows.
 * Matches shape expected by pages: { id, label, type, category, source }.
 */
export function buildListDescriptorsFromRows(rows) {
  const arr = Array.isArray(rows) ? rows : [];
  const groups = new Map();

  for (const r of arr) {
    const lid = String(r.list_id ?? "userCards").trim() || "userCards";
    if (!groups.has(lid)) {
      groups.set(lid, {
        id: lid,
        label: "",
        minSort: Number.POSITIVE_INFINITY,
        storyCount: 0,
        eventCount: 0,
      });
    }
    const g = groups.get(lid);
    const lab = typeof r.list_label === "string" ? r.list_label.trim() : "";
    if (lab && !g.label) g.label = lab;
    const rawSort = r.list_sort;
    const n = typeof rawSort === "number" ? rawSort : parseInt(rawSort, 10);
    if (!Number.isNaN(n)) g.minSort = Math.min(g.minSort, n);
    if ((r.content_type || "event") === "story") g.storyCount += 1;
    else g.eventCount += 1;
  }

  const descriptors = [...groups.values()].map((g) => ({
    id: g.id,
    label: g.label || humanizeListId(g.id),
    type: g.storyCount > 0 && g.storyCount >= g.eventCount ? "story" : "event",
    category: "C",
    source: "supabase",
    sort: g.minSort === Number.POSITIVE_INFINITY ? 0 : g.minSort,
  }));

  descriptors.sort((a, b) => {
    if (a.sort !== b.sort) return a.sort - b.sort;
    return String(a.id).localeCompare(String(b.id));
  });

  return descriptors;
}

/**
 * Build per-column card arrays aligned with descriptors order.
 */
export function buildActivityColumns(descriptors, rows) {
  const list = Array.isArray(rows) ? rows : [];
  return descriptors.map((c) => {
    const filtered = list.filter((r) => String(r.list_id ?? "userCards") === String(c.id));
    return sortRowsForColumnDisplay(filtered).map((item) => enrichStoryItem(item, c.type));
  });
}
